const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timezone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timezone);
  if (cached) return cached;
  const next = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  formatters.set(timezone, next);
  return next;
}

export function clockInTimeZone(date: Date, timezone: string): { hour: number; minute: number } {
  const parts = formatter(timezone).formatToParts(date);
  return {
    hour: Number(parts.find((part) => part.type === 'hour')?.value ?? 0),
    minute: Number(parts.find((part) => part.type === 'minute')?.value ?? 0),
  };
}

/** Returns the next occurrence of a lot-local wall-clock time, including DST boundaries. */
export function minutesUntilLocalClock(
  hour: number,
  minute: number,
  timezone: string,
  now = new Date(),
): number {
  if (
    !Number.isInteger(hour) ||
    hour < 0 ||
    hour > 23 ||
    !Number.isInteger(minute) ||
    minute < 0 ||
    minute > 59
  ) {
    return 0;
  }

  const targetMinute = hour * 60 + minute;
  const afterNow = Math.floor(now.getTime() / 60_000) * 60_000 + 60_000;
  const matches = (timestamp: number) => {
    const local = clockInTimeZone(new Date(timestamp), timezone);
    return local.hour === hour && local.minute === minute;
  };

  for (let offset = 0; offset <= 180; offset += 1) {
    const candidate = afterNow + offset * 60_000;
    if (matches(candidate)) return Math.ceil((candidate - now.getTime()) / 60_000);
  }

  const localNow = clockInTimeZone(now, timezone);
  let wallClockDelta = targetMinute - (localNow.hour * 60 + localNow.minute);
  if (wallClockDelta <= 0) wallClockDelta += 24 * 60;
  const guess = now.getTime() + wallClockDelta * 60_000;

  for (const dayOffset of [0, 24 * 60]) {
    for (let offset = -180; offset <= 180; offset += 1) {
      const candidate = guess + (dayOffset + offset) * 60_000;
      if (candidate > now.getTime() && matches(candidate)) {
        return Math.ceil((candidate - now.getTime()) / 60_000);
      }
    }
  }
  return 0;
}
